// swimlaneLanes — app-shell-local lane/row display helpers for the swimlane
// surface: the "colour by" lane tint, the humanized lane label, and per-row
// lane occupancy (for same-row arrow arcs + the entry dot). The lane-index and
// branch-hidden-row derivations moved to scene's `swimlaneTopology` (one source
// of truth, shared with the export composer); what stays here has no scene peer.
// Lifted out of the component so they're unit-testable directly (no render).
// Nothing here measures the DOM; every input is walk/layout-derived.

import type { Lane, Row } from "@archlang/engine";
import { displayLabel } from "../displayLabel.js";

/** "Colour by" lane tint: module lanes key into the coloring layer; actors
 *  (no module) stay neutral and are absent from the map. */
export function laneColorById(
  lanes: readonly Lane[],
  ownerColor?: (path: readonly string[]) => string | undefined,
): Map<string, string> {
  const m = new Map<string, string>();
  if (!ownerColor) return m;
  for (const l of lanes) {
    if (l.kind !== "module") continue;
    const c = ownerColor(l.qualifiedPath);
    if (c) m.set(l.id, c);
  }
  return m;
}

/** Lane id → humanized display label. Humanize at this single display boundary
 *  (§5.1); lane identity/matching uses the id, never this label. */
export function makeLaneLabel(lanes: readonly Lane[]): (id: string) => string {
  const m = new Map<string, string>();
  lanes.forEach((l) => m.set(l.id, displayLabel(l.label)));
  return (id: string) => m.get(id) ?? "";
}

export interface RowOccupancy {
  /** Does a block sit strictly between lanes `a` and `b` on row `y`? (A same-row
   *  arrow must arc over the top when so.) */
  readonly hasNodeBetween: (y: number, a: number, b: number) => boolean;
  /** Is `lane` occupied on row `y`? (Entry dot avoids a lane a serpentine merge
   *  dropped a block into.) */
  readonly laneOccupied: (y: number, lane: number) => boolean;
}

/** Occupied callee lanes per row, keyed by row y. */
export function makeRowOccupancy(rows: readonly Row[], laneIndex: (id: string) => number): RowOccupancy {
  const byRow = new Map<number, Set<number>>();
  for (const r of rows) {
    const li = laneIndex(r.toLaneId);
    if (li < 0) continue;
    let set = byRow.get(r.y);
    if (!set) byRow.set(r.y, (set = new Set()));
    set.add(li);
  }
  return {
    hasNodeBetween: (y: number, a: number, b: number): boolean => {
      const set = byRow.get(y);
      if (!set) return false;
      const lo = Math.min(a, b),
        hi = Math.max(a, b);
      for (let l = lo + 1; l < hi; l++) if (set.has(l)) return true;
      return false;
    },
    laneOccupied: (y: number, lane: number): boolean => byRow.get(y)?.has(lane) ?? false,
  };
}
